import { Box, Tab, Tabs } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useUserStore } from "store";
import { can } from "../../utils/access";
import UnsavedChangesDialog from "../shared/UnsavedChangesDialog";
import BookingSettingsTab from "../booking/BookingSettingsTab";
import FeedbackSettingsTab from "../feedback/FeedbackSettingsTab";
import ServiceRemindersTab from "../service_reminders/ServiceRemindersTab";
import { CustomTabPanel, a11yProps } from "./tabPanelUtils";
import FeaturesGeneralTab from "./FeaturesGeneralTab";
import CalendarSettingsTab from "./CalendarSettingsTab";
import FeaturesInboundDeliveriesTab from "./FeaturesInboundDeliveriesTab";
import FeaturesPartsSalesTab from "./FeaturesPartsSalesTab";
import FeaturesPurchaseLedgerTab from "./FeaturesPurchaseLedgerTab";
import FeaturesQuoteMachineTab from "./FeaturesQuoteMachineTab";
import PartnerBookingSettingsTab from "./PartnerBookingSettingsTab";
import ScheduleRemindersTab from "./ScheduleRemindersTab";
import TireHotelSettingsTab from "./TireHotelSettingsTab";
import TimeclockSettingsTab from "./TimeclockSettingsTab";

// Top-level "Ominaisuudet" (Features) settings tab.
// Renders a nested sub-tab layout: a "Yleiset" general page with the simple
// no-config toggles, plus one sub-tab per feature that needs extra configuration.
// Dirty state from the active sub-tab is bubbled up to MainSettingsView via
// onDirtyChange so the top-level tab-switch / route-leave guards keep working.
function OrganizationFeaturesTab({ t, onDirtyChange }) {
  const { currentOrganization } = useUserStore();
  const [subValue, setSubValue] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  // Sub-tab switch guard (mirrors MainSettingsView's top-level guard)
  const subTabDirtyRef = useRef(false);
  const subSaveFnRef = useRef(null);
  const [pendingSubTab, setPendingSubTab] = useState(null);
  const [showSubDialog, setShowSubDialog] = useState(false);
  const [subSwitchSaving, setSubSwitchSaving] = useState(false);

  const workHoursEnabled = currentOrganization?.work_hours_reporting_enabled ?? true;

  // Build the visible sub-tab list with gating. `perm` is the permission key the sub-tab needs;
  // most of them switch features on and off and stay admin-only (settings.features), while the
  // calendar colours are an everyday setting a foreman owns (settings.general).
  const subTabs = [
    {
      key: "general",
      perm: "settings.features",
      label: t("features.general_subtab"),
      render: (handleDirty) => <FeaturesGeneralTab onDirtyChange={handleDirty} />,
    },
    // Calendar colours and colour tags. No entitlement gate: every organization has a calendar.
    {
      key: "calendar",
      perm: "settings.general",
      label: t("features.calendar_subtab"),
      render: (handleDirty) => <CalendarSettingsTab onDirtyChange={handleDirty} />,
    },
  ];
  // Osamyynti is entitlement-gated. counter_sales_eligible is the superset gate:
  // Kassamyynti-only (Perus+) and full Osamyynti (Pro+) orgs both get the sub-tab,
  // since the shared parts_sales_enabled toggle lives here. The sub-tab appears for
  // eligible orgs even when the feature toggle is off, so the toggle itself lives here.
  if (currentOrganization?.counter_sales_eligible) {
    subTabs.push({
      key: "parts_sales",
      perm: "settings.features",
      label: t("features.parts_sales_subtab"),
      render: (handleDirty) => <FeaturesPartsSalesTab onDirtyChange={handleDirty} />,
    });
  }
  // Tarjousautomaatti is an entitlement (staff-set or plan-granted): the sub-tab holds the
  // workshop's own quote settings and appears only for organisations that have it.
  if (currentOrganization?.quote_machine_enabled) {
    subTabs.push({
      key: "quote_machine",
      perm: "settings.features",
      label: t("features.quote_machine_subtab"),
      render: (handleDirty) => <FeaturesQuoteMachineTab onDirtyChange={handleDirty} />,
    });
  }
  // Sähköiset lähetteet is available to every org (no entitlement flag) — the
  // sub-tab holds the org's inbox address and the support off-switch toggle.
  subTabs.push({
    key: "inbound_deliveries",
    perm: "settings.features",
    label: t("inboundDeliveries.settingsTabLabel"),
    render: (handleDirty) => <FeaturesInboundDeliveriesTab onDirtyChange={handleDirty} />,
  });
  if (currentOrganization?.tire_hotel_beta) {
    subTabs.push({
      key: "tire_hotel",
      perm: "settings.features",
      label: t("features.tire_hotel_subtab"),
      render: (handleDirty) => <TireHotelSettingsTab onDirtyChange={handleDirty} />,
    });
  }
  if (currentOrganization?.booking_beta) {
    subTabs.push({
      key: "booking",
      perm: "settings.features",
      label: t("booking.settings_tab"),
      render: (handleDirty) => <BookingSettingsTab onDirtyChange={handleDirty} />,
    });
  }
  // Varauskumppanit (booking portals such as Örum Munhuolto): staff-set entitlement
  // partner_booking_enabled. The workshop creates the API key it pastes into the portal here.
  if (currentOrganization?.partner_booking_enabled) {
    subTabs.push({
      key: "partner_booking",
      perm: "settings.features",
      label: t("partnerBooking.tabLabel"),
      render: (handleDirty) => <PartnerBookingSettingsTab onDirtyChange={handleDirty} />,
    });
  }
  subTabs.push({
    key: "feedback",
    perm: "settings.features",
    label: t("feedback_settings.tab_label"),
    render: (handleDirty) => <FeedbackSettingsTab onDirtyChange={handleDirty} />,
  });
  // Aikataulumuistutukset (schedule reminders) is available to every org (no
  // entitlement flag) — support off-switch, dialog defaults and message texts.
  subTabs.push({
    key: "schedule_reminders",
    perm: "settings.features",
    label: t("schedule_reminders.tab_label"),
    render: (handleDirty) => <ScheduleRemindersTab onDirtyChange={handleDirty} />,
  });
  // Huoltomuistutukset is entitlement-gated like Osamyynti (service_reminders_eligible
  // = admin-set service_reminders_beta or a gen-4 Pro/Enterprise plan grant) — the
  // sub-tab appears for eligible orgs even when the feature toggle is off, so the
  // toggle itself lives here.
  if (currentOrganization?.service_reminders_eligible) {
    subTabs.push({
      key: "service_reminders",
      perm: "settings.features",
      label: t("service_reminders.tab_label"),
      render: (handleDirty) => <ServiceRemindersTab onDirtyChange={handleDirty} />,
    });
  }
  // Ostoreskontra (purchase ledger) is entitlement-gated like Huoltomuistutukset:
  // purchase_ledger_eligible = admin-set beta flag or a later plan grant. The sub-tab holds
  // activation, the checklist, categories and the export, so it appears for eligible orgs
  // even while the feature itself is off.
  if (currentOrganization?.purchase_ledger_eligible) {
    subTabs.push({
      key: "purchase_ledger",
      perm: "settings.features",
      label: t("purchaseLedger.settings.tabLabel"),
      render: (handleDirty) => <FeaturesPurchaseLedgerTab onDirtyChange={handleDirty} />,
    });
  }
  if (workHoursEnabled) {
    // TimeclockSettingsTab auto-saves on toggle and never reports dirty state.
    subTabs.push({
      key: "timeclock",
      perm: "settings.features",
      label: t("worktime.settings.tab_label"),
      render: () => <TimeclockSettingsTab />,
    });
  }

  // Drop the sub-tabs this user may not open. A foreman reaches Ominaisuudet for the calendar
  // colours (settings.general) but not the feature switches (settings.features).
  const visibleSubTabs = subTabs.filter((tab) => !tab.perm || can(currentOrganization, tab.perm));

  // Clamp subValue if the gated list shrinks (e.g. work hours toggled off → Työaika removed).
  useEffect(() => {
    if (subValue >= visibleSubTabs.length) {
      setSubValue(0);
    }
  }, [visibleSubTabs.length, subValue]);

  // Deep link support: /settings?tab=features&subtab=booking selects a sub-tab once and
  // then drops the param so a later manual sub-tab switch is not fought by this effect.
  const subTabKeys = visibleSubTabs.map((tab) => tab.key).join(',');
  useEffect(() => {
    const requested = searchParams.get('subtab');
    if (!requested) return;
    const index = subTabKeys.split(',').indexOf(requested);
    if (index >= 0) {
      setSubValue(index);
    }
    const next = new URLSearchParams(searchParams);
    next.delete('subtab');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, subTabKeys]);

  // The active sub-tab reports its dirty/save here; we store it and re-bubble to the parent.
  const handleSubDirtyChange = useCallback(
    (dirty, saveFn) => {
      subTabDirtyRef.current = dirty;
      subSaveFnRef.current = saveFn;
      onDirtyChange?.(dirty, saveFn);
    },
    [onDirtyChange]
  );

  const doSwitchSubTab = useCallback(
    (newValue) => {
      // Clear stale dirty before mounting the next sub-tab. The newly mounted
      // reporting tab re-asserts its own state on mount; non-reporting tabs
      // (timeclock) stay clean.
      subTabDirtyRef.current = false;
      subSaveFnRef.current = null;
      onDirtyChange?.(false, null);
      setSubValue(newValue);
    },
    [onDirtyChange]
  );

  const handleSubChange = (event, newValue) => {
    if (subTabDirtyRef.current) {
      setPendingSubTab(newValue);
      setShowSubDialog(true);
    } else {
      doSwitchSubTab(newValue);
    }
  };

  const handleSubSwitchSave = useCallback(async () => {
    setSubSwitchSaving(true);
    try {
      if (subSaveFnRef.current) {
        await subSaveFnRef.current();
      }
      setShowSubDialog(false);
      if (pendingSubTab !== null) {
        const tab = pendingSubTab;
        setPendingSubTab(null);
        setTimeout(() => doSwitchSubTab(tab), 0);
      }
    } catch (err) {
      // Save failed — stay on current sub-tab
    } finally {
      setSubSwitchSaving(false);
    }
  }, [pendingSubTab, doSwitchSubTab]);

  const handleSubSwitchDiscard = useCallback(() => {
    setShowSubDialog(false);
    if (pendingSubTab !== null) {
      const tab = pendingSubTab;
      setPendingSubTab(null);
      setTimeout(() => doSwitchSubTab(tab), 0);
    }
  }, [pendingSubTab, doSwitchSubTab]);

  const handleSubSwitchCancel = useCallback(() => {
    setShowSubDialog(false);
    setPendingSubTab(null);
  }, []);

  const activeValue = subValue < visibleSubTabs.length ? subValue : 0;

  return (
    <Box sx={{ maxWidth: "100%", overflow: "hidden" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={activeValue}
          onChange={handleSubChange}
          aria-label="feature settings tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            "& .MuiTab-root": {
              minWidth: { xs: "auto", sm: 90 },
              px: { xs: 1.5, sm: 2 },
              fontSize: { xs: "13px", sm: "14px" },
            },
          }}
        >
          {visibleSubTabs.map((tab, index) => (
            <Tab key={tab.key} label={tab.label} {...a11yProps(index, "features")} />
          ))}
        </Tabs>
      </Box>

      {visibleSubTabs.map((tab, index) => (
        <CustomTabPanel key={tab.key} value={activeValue} index={index} idPrefix="features">
          {tab.render(handleSubDirtyChange)}
        </CustomTabPanel>
      ))}

      <UnsavedChangesDialog
        open={showSubDialog}
        onSave={handleSubSwitchSave}
        onDiscard={handleSubSwitchDiscard}
        onCancel={handleSubSwitchCancel}
        saving={subSwitchSaving}
      />
    </Box>
  );
}

export default withTranslation()(OrganizationFeaturesTab);
